import { api } from '../lib/api';
import { dollars } from '../lib/format';
import type {
  AcceptInviteResponse,
  BillingInterval,
  BillingPortalResponse,
  CreateInviteResponse,
  CreateOrgResponse,
  OrgDetail,
  OrgSummary,
  Pricing,
  Role,
  SeatLevel,
  SubscriptionStatus,
} from '../lib/types';

export interface OrgsResponse {
  orgs: OrgSummary[];
  pricing: Pricing;
}

export function fetchOrgs(): Promise<OrgsResponse> {
  return api<OrgsResponse>('/v1/orgs');
}

export function fetchOrg(orgId: string): Promise<OrgDetail> {
  return api<OrgDetail>(`/v1/orgs/${orgId}`);
}

export function createOrg(
  name: string,
  interval: BillingInterval = 'month',
  team: { seats?: number; invites?: string[] } = {},
): Promise<CreateOrgResponse> {
  return api<CreateOrgResponse>('/v1/orgs', { method: 'POST', body: { name, interval, ...team } });
}

export function inviteMember(
  orgId: string,
  input: { email: string; name?: string; role: Role; seat: SeatLevel },
): Promise<CreateInviteResponse> {
  return api<CreateInviteResponse>(`/v1/orgs/${orgId}/invites`, {
    method: 'POST',
    body: input,
  });
}

export function acceptInvite(token: string): Promise<AcceptInviteResponse> {
  return api<AcceptInviteResponse>(`/v1/invites/${token}/accept`, { method: 'POST' });
}

/** `role: null` removes the member. */
export function setMemberRole(
  orgId: string,
  userId: string,
  role: Role | null,
): Promise<{ ok: boolean; seats: number }> {
  return api<{ ok: boolean; seats: number }>(`/v1/orgs/${orgId}/members/${userId}`, {
    method: 'PATCH',
    body: { role },
  });
}

export function setMemberSeat(
  orgId: string,
  userId: string,
  seat: SeatLevel,
): Promise<{ ok: boolean; seats: number }> {
  return api<{ ok: boolean; seats: number }>(`/v1/orgs/${orgId}/members/${userId}`, {
    method: 'PATCH',
    body: { seat },
  });
}

/** What changing to N seats would cost, before it happens. */
export interface SeatQuote {
  seats: number;
  current: number;
  used: number;
  /** False on a trial with no card yet: nothing is due until billing is set up. */
  billed: boolean;
  dueNowCents: number;
  monthlyCents: number;
  periodEnd: string | null;
  currency: string;
}

export function previewSeats(orgId: string, seats: number): Promise<SeatQuote> {
  return api<SeatQuote>(`/v1/orgs/${orgId}/seats/preview?seats=${seats}`);
}

/** How many paid seats the team pays for. Owner only; Stripe prorates. */
export function setSeatCount(orgId: string, seats: number): Promise<{ seats: number; seatsUsed: number }> {
  return api<{ seats: number; seatsUsed: number }>(`/v1/orgs/${orgId}/seats`, { method: 'PUT', body: { seats } });
}

export function seatLabel(seat: SeatLevel): string {
  return seat === 'paid' ? 'Paid seat' : 'Free';
}

export function billingPortal(orgId: string): Promise<BillingPortalResponse> {
  return api<BillingPortalResponse>(`/v1/orgs/${orgId}/billing-portal`);
}

/** Stripe Checkout for an org with no subscription yet, or one that lapsed. */
export function startCheckout(orgId: string, interval?: BillingInterval): Promise<{ url: string }> {
  return api<{ url: string }>(`/v1/orgs/${orgId}/checkout`, { method: 'POST', body: interval ? { interval } : {} });
}

/** What switching to monthly or yearly would cost right now. */
export interface IntervalQuote {
  interval: BillingInterval;
  current: BillingInterval;
  billed: boolean;
  dueNowCents: number;
  perPeriodCents: number;
  periodEnd: string | null;
}

export function previewInterval(orgId: string, interval: BillingInterval): Promise<IntervalQuote> {
  return api<IntervalQuote>(`/v1/orgs/${orgId}/interval/preview?interval=${interval}`);
}

export function setBillingInterval(orgId: string, interval: BillingInterval): Promise<{ interval: BillingInterval }> {
  return api<{ interval: BillingInterval }>(`/v1/orgs/${orgId}/interval`, { method: 'PUT', body: { interval } });
}

/** The trial that comes with an interval, if any: "7-day free trial" or null. */
export function trialLabel(pricing: Pricing | null, interval: BillingInterval): string | null {
  if (!pricing || pricing.trialDays <= 0) return null;
  if ((pricing.trialInterval ?? 'year') !== interval) return null;
  return `${pricing.trialDays}-day free trial`;
}

/** "$9 per seat per month" or "$90 per seat per year", from the pricing payload. */
export function seatPriceLabel(pricing: Pricing | null, interval: BillingInterval): { price: string; cadence: string } {
  if (!pricing) return { price: interval === 'year' ? '$90' : '$9', cadence: `per seat per ${interval}` };
  const cents = interval === 'year' && pricing.seatYearlyCents !== null ? pricing.seatYearlyCents : pricing.seatMonthlyCents;
  return { price: dollars(cents), cadence: interval === 'year' ? 'per seat per year' : 'per seat per month' };
}

/** How much a year saves against twelve months, as a whole percent; 0 when it does not. */
export function yearlySavingsPercent(pricing: Pricing | null): number {
  if (!pricing || pricing.seatYearlyCents === null) return 0;
  const monthly = pricing.seatMonthlyCents * 12;
  return monthly > 0 ? Math.max(0, Math.round((1 - pricing.seatYearlyCents / monthly) * 100)) : 0;
}

type PlanDates = Pick<OrgSummary, 'id' | 'cancelAt' | 'renewsAt' | 'subscriptionStatus'>;

/** Wind the plan down at the end of the period. */
export function cancelPlan(orgId: string): Promise<PlanDates> {
  return api<PlanDates>(`/v1/orgs/${orgId}/cancel`, { method: 'POST' });
}

/** Change your mind before it stops. */
export function resumePlan(orgId: string): Promise<PlanDates> {
  return api<PlanDates>(`/v1/orgs/${orgId}/resume`, { method: 'POST' });
}

export function revokeInvite(orgId: string, inviteId: string): Promise<void> {
  return api<void>(`/v1/orgs/${orgId}/invites/${inviteId}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Role and subscription helpers: the whole role-aware UI reads from these.
// ---------------------------------------------------------------------------

export const ROLE_ORDER: Record<Role, number> = { user: 0, admin: 1, owner: 2 };
export const ROLES: Role[] = ['user', 'admin', 'owner'];

export function roleTone(role: Role): 'info' | 'neutral' {
  return role === 'user' ? 'neutral' : 'info';
}

export function atLeast(role: Role | undefined, minimum: Role): boolean {
  return role !== undefined && ROLE_ORDER[role] >= ROLE_ORDER[minimum];
}

export function roleLabel(role: Role): string {
  switch (role) {
    case 'owner':
      return 'Owner';
    case 'admin':
      return 'Admin';
    default:
      return 'Member';
  }
}

export function roleDescription(role: Role): string {
  switch (role) {
    case 'owner':
      return 'Everything an admin can do, plus billing and the subscription.';
    case 'admin':
      return 'Sees every recording in the org, and manages members.';
    default:
      return 'Records into the team library and can watch everything in it.';
  }
}

export interface SubscriptionNotice {
  tone: 'caution' | 'danger' | 'info';
  title: string;
  body: string;
  /** Owners get an action; everyone else gets the explanation only. */
  ownerAction?: string;
}

/**
 * Trial and past-due states are banners, never walls. A team that forgot to
 * add a card should still be able to watch everything they already recorded.
 */
export function subscriptionNotice(
  status: SubscriptionStatus,
  trialEndsAt: string | null,
): SubscriptionNotice | null {
  if (status === 'active') return null;

  if (status === 'incomplete') {
    return {
      tone: 'info',
      title: 'Finish checkout to start the plan',
      body: 'The team is named but there is no plan behind it yet. Nothing can be recorded into it or invited onto it until checkout is done.',
      ownerAction: 'Finish checkout',
    };
  }

  if (status === 'trialing') {
    const ends = trialEndsAt ? new Date(trialEndsAt).getTime() : null;
    if (!ends) return null;
    const daysLeft = Math.ceil((ends - Date.now()) / 86_400_000);
    if (daysLeft > 5) return null;
    return {
      tone: 'caution',
      title: daysLeft <= 0 ? 'Your trial has ended' : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left on your trial`,
      body:
        daysLeft <= 0
          ? 'Add a payment method to keep recording for this team. Nothing you have already recorded goes away.'
          : 'Add a payment method whenever you like; everything keeps working until then.',
      ownerAction: 'Set up billing',
    };
  }

  if (status === 'past_due') {
    return {
      tone: 'danger',
      title: 'Payment failed',
      body:
        'New recordings for this team are paused until the subscription is current. Everything already here stays watchable.',
      ownerAction: 'Update payment method',
    };
  }

  return {
    tone: 'danger',
    title: 'Subscription canceled',
    body:
      'This team is read-only. Existing recordings and share links keep working; new ones need an active subscription.',
    ownerAction: 'Restart the subscription',
  };
}

/** Can anybody record into this org right now? Mirrors the API's rule. */
export function orgAcceptsRecordings(status: SubscriptionStatus): boolean {
  return status === 'trialing' || status === 'active';
}
